import type { Unsubscribe } from 'firebase/firestore'
import { doc, getDocFromServer, onSnapshot } from 'firebase/firestore'
import React from 'react'

import firestore from './firebase-db'

const PROBE_INTERVAL = 15000
const PROBE_TIMEOUT = 8000
const PROBE_BASE = '/release/'
const PROBE_DOC = 'remote'

const withTimeout = <T>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([
    p,
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error('probe-timeout')), ms)
    }),
  ])

const initFirebaseSnapshot = (baseQuery: string, ...queryInit: string[]) => {
  const unsub = React.useRef<Unsubscribe>()

  const [firebaseData, setFirebaseData] = React.useState<any>(undefined)

  const probeConnection = async (): Promise<boolean> => {
    if (!firestore) return false
    try {
      const snap = await withTimeout(
        getDocFromServer(doc(firestore, PROBE_BASE, PROBE_DOC)),
        PROBE_TIMEOUT
      )
      console.log('Firestore probe OK at:', new Date(), {
        exists: snap.exists(),
      })
      return true
    } catch (e) {
      const message = 'Firestore probe failed/timed out — offline.'
      console.warn(message, new Date(), e)
      return false
    }
  }

  const createSnapShot = () => {
    if (firestore) {
      unsub.current = onSnapshot(
        doc(firestore, baseQuery, ...queryInit),
        (doc) => {
          if (!doc.exists()) {
            setFirebaseData(undefined)
          }
          setFirebaseData(doc.data())
        }
      )
    }
  }

  const validateQuery = (query: string[]) =>
    query.filter((res) => res).length >= 3

  React.useMemo(() => {
    if (baseQuery && validateQuery(queryInit)) {
      createSnapShot()
    }
    return () => unsub.current && unsub.current()
  }, [[baseQuery, ...queryInit].toString()])

  React.useMemo(() => {
    if (!baseQuery || !validateQuery(queryInit)) {
      return undefined
    }
    const id = setInterval(async () => {
      await probeConnection()
    }, PROBE_INTERVAL)
    return () => id && clearInterval(id)
  }, [[baseQuery, ...queryInit].toString()])

  return firebaseData
}

export default initFirebaseSnapshot
